import { optionalCounterpartyEmailSchema } from "@common/schemas/counterparty.schema";
import { CreateCounterpartyAddressBody } from "@common/schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "@common/schemas/counterpartyContact.schema";
import { personNamesNotOnRecord } from "@common/helpers/counterpartyDetailChanges";
import {
    groupContactDetails,
    parsePartyAddress,
} from "@common/helpers/extractedPartyFields";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { FormSchema } from "@components/counterparty/counterpartyFormSchema";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { getAccrualExtraction } from "./accrualImporter.helpers";
import {
    AccrualPartyHints,
    EMPTY_PARTY_HINTS,
    getPartyHintsFromOriginalExtraction,
} from "./getPartyHintsFromOriginalExtraction";
import { getExtractionOrderIndex } from "./useAccrualImporterCounterpartyEditor.helpers";

export interface CounterpartyOcrPrefill {
    initialValues: Partial<FormSchema>;
    contacts: CreateCounterpartyContactBody[];
    addresses: CreateCounterpartyAddressBody[];
}

function firstNonEmpty(values: Array<string | null | undefined>): string {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) {
            return trimmed;
        }
    }
    return "";
}

function usableEmail(value: string): string | null {
    const parsed = optionalCounterpartyEmailSchema.safeParse(value);
    if (!parsed.success || !parsed.data) {
        return null;
    }
    return parsed.data;
}

function contactNamesForParty({
    hints,
    partyName,
    emails,
}: {
    hints: AccrualPartyHints;
    partyName: string;
    emails: string[];
}): string[] {
    const people = personNamesNotOnRecord({
        names: hints.names,
        savedNames: partyName ? [partyName] : [],
    });
    if (people.length > 0) {
        return people;
    }
    if (
        !partyName ||
        (emails.length === 0 && hints.phoneNumbers.length === 0)
    ) {
        return [];
    }
    return [partyName];
}

function contactsFromHints({
    hints,
    partyName,
}: {
    hints: AccrualPartyHints;
    partyName: string;
}): CreateCounterpartyContactBody[] {
    const emails = hints.emails.flatMap((email) => {
        const usable = usableEmail(email);
        return usable ? [usable] : [];
    });
    const { first, extras } = groupContactDetails({
        emails,
        phoneNumbers: hints.phoneNumbers,
        contactNames: contactNamesForParty({ hints, partyName, emails }),
        fallbackContactName: partyName || null,
        isFirstPrimary: true,
    });
    return first ? [first, ...extras] : extras;
}

function addressesFromHints(
    hints: AccrualPartyHints,
): CreateCounterpartyAddressBody[] {
    return hints.addresses.map((address, index) => ({
        ...parsePartyAddress(address),
        isPrimary: index === 0,
    }));
}

export function buildCounterpartyPrefillFromPartyHints({
    hints,
    name,
}: {
    hints: AccrualPartyHints;
    name?: string | null;
}): CounterpartyOcrPrefill {
    const displayName = firstNonEmpty([hints.name, name]);
    const contacts = contactsFromHints({ hints, partyName: displayName });
    const addresses = addressesFromHints(hints);
    const address = addresses[0];
    const contact = contacts[0];
    const initialValues: Partial<FormSchema> = {};

    if (displayName) {
        initialValues.name = displayName;
    }
    if (contact?.email) {
        initialValues.email = contact.email;
    }
    if (contact?.phoneNumber) {
        initialValues.phoneNumber = contact.phoneNumber;
    }
    if (address?.street) {
        initialValues.street = address.street;
    }
    if (address?.street2) {
        initialValues.street2 = address.street2;
    }
    if (address?.city) {
        initialValues.city = address.city;
    }
    if (address?.state) {
        initialValues.state = address.state;
    }
    if (address?.postalCode) {
        initialValues.postalCode = address.postalCode;
    }
    if (address?.country) {
        initialValues.country = address.country;
    }

    return { initialValues, contacts, addresses };
}

export function buildAccrualCounterpartyCreatePrefill({
    financialDocument,
    extractionId,
    counterpartyName,
    mode,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId: string | null;
    counterpartyName?: string | null;
    mode: AccrualDocumentImporterMode;
}): CounterpartyOcrPrefill {
    const orderIndex =
        extractionId == null
            ? null
            : getExtractionOrderIndex({
                  financialDocument,
                  extractionId,
                  mode,
              });
    const hints =
        orderIndex == null
            ? EMPTY_PARTY_HINTS
            : getPartyHintsFromOriginalExtraction({
                  originalExtraction:
                      getAccrualExtraction(financialDocument)
                          ?.originalExtraction,
                  orderIndex,
                  mode,
              });

    return buildCounterpartyPrefillFromPartyHints({
        hints,
        name: counterpartyName,
    });
}
